import { ZyNodeViewWrapper } from '@/components/common/node-view-wrapper';
import { Button } from '@/components/tiptap-editor/tiptap-ui-primitive/button';
import { useIsEditorReadonly } from '@/hooks/use-is-editor-readonly';
import { dispatchMetaTransaction } from '@/lib/zy-tiptap-utils';
import * as Checkbox from '@radix-ui/react-checkbox';
import { type ReactNodeViewProps, NodeViewContent } from '@tiptap/react';
import { CheckIcon, XIcon } from 'lucide-react';
import { memo, useCallback } from 'react';

const DetectAnswerOptionNodeView: React.FC<ReactNodeViewProps> = ({ node, editor, updateAttributes, deleteNode, decorations }) => {
    const isEditorReadonly = useIsEditorReadonly(editor);
    const { isCorrect, optionId, text } = node.attrs;

    const handleToggleCorrect = useCallback(() => {
        const tr = editor.state.tr;
        let changed = false;

        editor.state.doc.descendants((docNode, pos) => {
            if (docNode.type.name !== 'detectAnswerOption') {
                return;
            }

            const nextCorrect = docNode.attrs.optionId === optionId;
            if (docNode.attrs.isCorrect !== nextCorrect) {
                tr.setNodeMarkup(pos, undefined, {
                    ...docNode.attrs,
                    isCorrect: nextCorrect,
                });
                changed = true;
            }
        });

        if (changed) {
            editor.view.dispatch(tr);
            dispatchMetaTransaction(editor, { detectAnswerOptionUpdate: true });
            return;
        }

        updateAttributes({ isCorrect: !isCorrect });
    }, [editor, isCorrect, optionId, updateAttributes]);

    const handleDelete = useCallback(() => {
        if (window.confirm('Are you sure you want to delete this option?')) {
            editor.commands.removeDetectAnswerOption(optionId);
            deleteNode();
            dispatchMetaTransaction(editor, { detectAnswerOptionUpdate: true });
        }
    }, [deleteNode, editor, optionId]);

    return (
        <ZyNodeViewWrapper decorations={decorations}>
            <div
                className={`relative my-2 flex items-start gap-3 rounded-lg border-2 p-2 pr-8 transition-all ${
                    isCorrect
                        ? 'border-green-500 bg-green-50 dark:border-green-600 dark:bg-green-950/20'
                        : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900'
                }`}
            >
                <div
                    className="mt-0.5 flex shrink-0 items-center gap-2"
                    contentEditable={false}
                >
                    <Checkbox.Root
                        checked={isCorrect}
                        disabled={isEditorReadonly}
                        onCheckedChange={handleToggleCorrect}
                        onClick={event => event.stopPropagation()}
                        onMouseDown={event => event.preventDefault()}
                        aria-label="Mark option as correct"
                        title="Mark option as correct"
                        className={`flex h-5 w-5 cursor-pointer items-center justify-center rounded-full border-2 transition-colors ${
                            isCorrect
                                ? 'border-green-500 bg-green-500 dark:border-green-600 dark:bg-green-600'
                                : 'border-gray-300 bg-white dark:border-gray-600 dark:bg-gray-800'
                        } hover:border-gray-400 focus:ring-2 focus:ring-green-500 focus:ring-offset-2 focus:outline-none dark:hover:border-gray-500 dark:focus:ring-offset-gray-900`}
                    >
                        <Checkbox.Indicator>
                            <CheckIcon className="h-3 w-3 text-white" />
                        </Checkbox.Indicator>
                    </Checkbox.Root>
                </div>

                <div className="w-full">
                    <div
                        className="mb-2 whitespace-pre-wrap rounded border border-dashed border-gray-300 p-2 text-sm text-gray-700 dark:border-gray-700 dark:text-gray-200"
                        contentEditable={false}
                    >
                        {text || 'Option'}
                    </div>
                    <NodeViewContent />
                </div>

                <div
                    className="absolute top-2 right-0"
                    contentEditable={false}
                >
                    <Button
                        onClick={handleDelete}
                        className="flex h-6 w-6 items-center justify-center rounded-full text-gray-400 transition-all hover:bg-red-50 hover:text-red-600 dark:text-gray-500 dark:hover:bg-red-950/20 dark:hover:text-red-500"
                        data-style="ghost"
                        aria-label="Delete option"
                        disabled={isEditorReadonly}
                        tooltip="Delete option"
                    >
                        <XIcon className="h-4 w-4" />
                    </Button>
                </div>
            </div>
        </ZyNodeViewWrapper>
    );
};

export default memo(DetectAnswerOptionNodeView);
